import { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback, lazy, Suspense } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Container,
  TextInput,
  Box,
  Stack,
  Text,
  Title,
  Flex,
  Button,
  Paper,
  rem,
  useMantineTheme,
  ActionIcon,
  Group,
  Center,
  Loader,
} from "@mantine/core";
import { useDebouncedValue, useMediaQuery } from "@mantine/hooks";
import { IconSearch, IconX } from "@tabler/icons-react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { type CompanyWithUI } from "../../components/shared/CompanyCardHorizontal";
import { handleTrpcError } from "../../shared/utils/trpc/handletrpcError.util.ts";
import { useAuth } from "@/hooks/useAuth";
import PaginationSignupModal from "@/components/shared/UserSignupModal";
import type { DiscoverPromoSlide } from "./components/discoverPromoCarousel.shared";
import DiscoverPromoCarouselDesktop from "./components/DiscoverPromoCarouselDesktop";
import DiscoverPromoCarouselMobile from "./components/DiscoverPromoCarouselMobile";
import DiscoverPromoCarouselPlaceholder from "./components/DiscoverPromoCarouselPlaceholder";
import {
  AUTH_DISCOVER_PAGE_SIZE,
  DEFAULT_PAGE_SIZE,
  DISCOVER_PAGE_CONTENT_PX,
  DISCOVER_PAGE_CONTENT_SIZE,
  DISCOVER_PAGE_SIZE_OPTIONS,
  LOADER_MIN_VISIBLE_MS,
  LOADER_SHOW_DELAY_MS,
  MAX_RETRIES,
  QUICK_SEARCH_DEBOUNCE_MS,
  MOBILE_PROMO_STRIP_PX,
  MOBILE_PROMO_STRIP_RESERVE,
} from "./DiscoverPage.constants";
import { remapPageIndex } from "./companyGridPageSize.util";
import { useCompactCompanyGridColumns } from "./hooks/useCompactCompanyGridColumns";
import { readGuestShortlistCompanyIds } from "@/shared/utils/guestShortlistStorage";
import { getSectorBySlug } from "@/pages/home/sectorCatalog";
import { useUser } from "@/context/UserContext";
import { useSmoothLoading } from "./hooks/useSmoothLoading";
import { useDiscoverPromoSlides } from "./hooks/useDiscoverPromoSlides";
import { useTypingPlaceholder } from "./hooks/useTypingPlaceholder";
import { deriveSearchByFromQuery } from "./utils/discoverSearch";
import {
  discoverBrowseQueryKey,
  discoverSearchQueryKey,
  fetchDiscoverBrowse,
  fetchDiscoverSearch,
  type DiscoverRouteMode,
} from "./hooks/discoverCompanies.api";
import DiscoverResultsSection from "./components/DiscoverResultsSection";
import DiscoverGlobalEcosystemsSection from "./components/DiscoverGlobalEcosystemsSection";
import DiscoverActiveCollaborationHero from "./components/DiscoverActiveCollaborationHero";
import { useExploreTabState } from "./components/DiscoverExploreTabs";
import {
  EXPLORE_TAB_SUBTITLES,
  guestExploreTabHref,
  isExploreTabSwitchState,
  isGuestExploreListPath,
  resolveExploreTab,
  type ExploreTab,
} from "./discoverExplore.const";
import { useVerificationModalOpener } from "@/components/layout/AppLayout";
import { AUTH_MOBILE_EXPLORE_ROW_HEIGHT } from "@/components/layout/AuthExploreTopHeader";
import { landingNavbarOffset } from "@/components/layout/LandingNavbar";
import {
  getExpoSignupSlug,
  isExpoSignupUser,
  shouldPromptCompanyRegistryVerification,
} from "@/shared/constants/clientOnboarding.const";
import { PageCtaSlotList } from "@/components/shared/PageCtaSlotList";
import PromoPopHost from "@/components/promo-pop/PromoPopHost";
import type { PromoPopPageKey } from "@shared/promoPopPages";
import { PageCtaAfterFirstRow } from "@/components/shared/ExploreGridWithAfterFirstRowCta";
import type { ReactNode } from "react";

/** Tab bodies — lazy so guest `/` does not download auth explore columns until needed (P5). */
const ExpertsColumn = lazy(() => import("@/pages/experts-and-corridors/components/ExpertsColumn"));
const CorridorsColumn = lazy(() => import("@/pages/experts-and-corridors/components/CorridorsColumn"));
const GuestExpertsExploreColumn = lazy(() => import("@/pages/discover/guest/GuestExpertsExploreColumn"));
const GuestCorridorsExploreColumn = lazy(
  () => import("@/pages/discover/guest/GuestCorridorsExploreColumn"),
);
const DiscoverExpoSection = lazy(() => import("./components/DiscoverExpoSection"));

/** Logo/burger row height of AppLayout's mobile header (explore row sits below it). */
const AUTH_MOBILE_HEADER_BASE_PX = 60;
const TAB_SWITCH_SCROLL_GAP_PX = 8;
/** Lets the tab-switch scroll reach the anchor while lazy tab bodies are still loading. */
const TAB_BODY_MOBILE_MIN_H = { base: "100dvh", sm: 0 };

const TAB_SWITCH_SETTLE_MS = 1500;

/**
 * Scrolls the anchor to `offsetPx` below the viewport top, then keeps it there while the hero
 * and lazy tab bodies settle (layout shifts after mount). Stops on the first user scroll gesture.
 */
function pinScrollToAnchor(getAnchor: () => HTMLElement | null, offsetPx: number) {
  let stopped = false;
  const align = () => {
    const anchor = getAnchor();
    if (stopped || !anchor) return;
    const top = anchor.getBoundingClientRect().top + window.scrollY - offsetPx;
    window.scrollTo({ top: Math.max(0, top), behavior: "auto" });
  };
  const resizeObserver = new ResizeObserver(align);
  const stop = () => {
    stopped = true;
    resizeObserver.disconnect();
    window.removeEventListener("touchstart", stop);
    window.removeEventListener("wheel", stop);
  };
  window.addEventListener("touchstart", stop, { passive: true });
  window.addEventListener("wheel", stop, { passive: true });
  // Two frames: runs after ScrollToTop's reset on guest path changes.
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      align();
      if (!stopped) resizeObserver.observe(document.body);
    });
  });
  window.setTimeout(stop, TAB_SWITCH_SETTLE_MS);
}

function ExploreTabSuspense({ children }: { children: ReactNode }) {
  return (
    <Suspense
      fallback={
        <Center py="xl">
          <Loader size="md" color="primary" />
        </Center>
      }
    >
      {children}
    </Suspense>
  );
}

export type DiscoverPageProps = {
  /** When set and non-empty, replaces the default promo carousel data. UI stays in `DiscoverPromoCarousel`. */
  promoCarouselSlides?: DiscoverPromoSlide[];
};

export default function DiscoverPage({ promoCarouselSlides }: DiscoverPageProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [, setSearchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const { addToShortlist, removeFromShortlist, userDetails, shortlistedIds } = useUser();
  const openVerificationModal = useVerificationModalOpener();
  const theme = useMantineTheme();
  const isLgUp = useMediaQuery(`(min-width: ${theme.breakpoints.lg})`, false, { getInitialValueInEffect: true });
  const isSmUp = useMediaQuery(`(min-width: ${theme.breakpoints.sm})`, false, { getInitialValueInEffect: true });
  const isMdUp = useMediaQuery(`(min-width: ${theme.breakpoints.md})`, false, { getInitialValueInEffect: true });
  const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)", false, {
    getInitialValueInEffect: true,
  });
  const isHomeRoute = location.pathname === "/";
  const isGuestDiscoverRoute = !isAuthenticated && isGuestExploreListPath(location.pathname);
  const isFacilitatorDiscoveryRoute = location.pathname.startsWith("/facilitator/my-discovery");
  const isEcosystemHeadDiscoveryRoute = location.pathname.startsWith("/ecosystem-head/my-discovery");
  const isExpertDiscoveryRoute = location.pathname.startsWith("/expert/my-discovery");
  const isExpoHeadDiscoveryRoute = location.pathname.startsWith("/expo-head/explore");
  const isPartnerPortalDiscoveryRoute =
    isFacilitatorDiscoveryRoute ||
    isEcosystemHeadDiscoveryRoute ||
    isExpertDiscoveryRoute ||
    isExpoHeadDiscoveryRoute;
  const isMyDiscoveryRoute =
    location.pathname.startsWith("/my-discovery") || isPartnerPortalDiscoveryRoute;
  const isClientMyDiscoveryRoute =
    location.pathname.startsWith("/my-discovery") && !isPartnerPortalDiscoveryRoute;
  const expoSignupSlug = getExpoSignupSlug(userDetails);
  const expoSignupLocked = isExpoSignupUser(userDetails);

  const resolveDiscoverMode = useCallback((): DiscoverRouteMode => {
    if (expoSignupLocked && expoSignupSlug) return "expoDirectory";
    if (isFacilitatorDiscoveryRoute) return "facilitatorSearch";
    if (isExpertDiscoveryRoute) return "expertSearch";
    if (isExpoHeadDiscoveryRoute) return "expoHeadSearch";
    if (isEcosystemHeadDiscoveryRoute) return "ecosystemHeadSearch";
    if (isAuthenticated && isMyDiscoveryRoute && !isPartnerPortalDiscoveryRoute) return "discover";
    return "listAll";
  }, [
    expoSignupLocked,
    expoSignupSlug,
    isFacilitatorDiscoveryRoute,
    isExpertDiscoveryRoute,
    isExpoHeadDiscoveryRoute,
    isEcosystemHeadDiscoveryRoute,
    isAuthenticated,
    isMyDiscoveryRoute,
    isPartnerPortalDiscoveryRoute,
  ]);
  const showExploreTabs =
    !expoSignupLocked && ((isClientMyDiscoveryRoute && isAuthenticated) || isGuestDiscoverRoute);
  const { activeTab: queryActiveTab, setActiveTab: setQueryActiveTab } = useExploreTabState();
  const activeTab: ExploreTab = isGuestDiscoverRoute
    ? (resolveExploreTab(location.pathname, location.search) ?? "companies")
    : queryActiveTab;
  const setActiveTab = useCallback(
    (tab: ExploreTab) => {
      if (isGuestDiscoverRoute) {
        navigate(guestExploreTabHref(tab));
        return;
      }
      setQueryActiveTab(tab);
    },
    [isGuestDiscoverRoute, navigate, setQueryActiveTab],
  );
  const isCompaniesTab = !showExploreTabs || activeTab === "companies";
  const promoPopPageKey: PromoPopPageKey | null = !showExploreTabs
    ? "DISCOVER"
    : activeTab === "experts"
      ? "EXPERTS"
      : activeTab === "corridors"
        ? "CORRIDORS"
        : activeTab === "expos"
          ? "EXPO_DIRECTORY"
          : activeTab === "companies"
            ? "DISCOVER"
            : null;
  const useCompactCompanyGrid = isGuestDiscoverRoute || isMyDiscoveryRoute;
  const shouldShowPromoCarousel = !isAuthenticated && !isGuestDiscoverRoute;
  const hasPromoCarouselOverride = Boolean(promoCarouselSlides && promoCarouselSlides.length > 0);
  const { slides: fetchedPromoSlides, loading: promoSlidesLoading } = useDiscoverPromoSlides(
    shouldShowPromoCarousel && !hasPromoCarouselOverride,
  );
  const showPromoCarouselPlaceholder =
    shouldShowPromoCarousel && !hasPromoCarouselOverride && promoSlidesLoading;

  /** Mobile (viewport below lg): when true, promo sits in-flow after pagination; when false, fixed strip at viewport bottom. Desktop uses the top-of-page carousel. */
  const [mobileDocked, setMobileDocked] = useState(false);
  const mobileDockSentinelRef = useRef<HTMLDivElement>(null);

  const [searchQuery, setSearchQuery] = useState("");
  /** Browse `quickSearch` and AI tab requests use this so we do not hit the API on every keystroke. */
  const [debouncedSearch] = useDebouncedValue(searchQuery, QUICK_SEARCH_DEBOUNCE_MS);
  const latestQuickSearchKeyRef = useRef<string>("");
  const inFlightListAllKeyRef = useRef<string | null>(null);
  const inFlightQuickSearchKeyRef = useRef<string | null>(null);

  const derivedSearchBy = useMemo(() => deriveSearchByFromQuery(searchQuery), [searchQuery]);
  const searchTypingPlaceholder = useTypingPlaceholder(EXPLORE_TAB_SUBTITLES.companies, {
    disabled: prefersReducedMotion || Boolean(searchQuery.trim()),
    // Continuous type/delete + cursor blink re-renders the whole Discover tree (~24fps) and tanks Lighthouse.
    loop: false,
  });
  const searchPlaceholder =
    derivedSearchBy === "gtid" ? "Enter GTID" : searchQuery ? "" : searchTypingPlaceholder;
  const [companies, setCompanies] = useState<CompanyWithUI[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(
    isAuthenticated ? AUTH_DISCOVER_PAGE_SIZE : DEFAULT_PAGE_SIZE,
  );
  const tableHeaderAnchorRef = useRef<HTMLDivElement>(null);
  const tabContentAnchorRef = useRef<HTMLDivElement>(null);
  const { ref: gridContainerRef, columns: companyGridColumns } = useCompactCompanyGridColumns();
  const companyFirstRowCount = useCompactCompanyGrid ? companyGridColumns : isMdUp ? 2 : 1;
  const lockDiscoverPageSize = isHomeRoute || isGuestDiscoverRoute || useCompactCompanyGrid;
  const effectivePageSize = lockDiscoverPageSize ? DEFAULT_PAGE_SIZE : pageSize;
  const prevEffectivePageSizeRef = useRef(effectivePageSize);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [matchCount, setMatchCount] = useState<number | null>(null);
  const [paginationSignupModalOpened, setPaginationSignupModalOpened] = useState(false);
  const [guestShortlistedIds, setGuestShortlistedIds] = useState<Set<string>>(new Set());
  const hasHydratedFromUrlRef = useRef(false);
  const skipNextUrlSyncRef = useRef(false);
  const hydratedPageRef = useRef(1);
  const lastFetchedCriteriaRef = useRef<string | null>(null);
  const lastListRequestKeyRef = useRef<string | null>(null);

  const [selectedSectorTitle, setSelectedSectorTitle] = useState<string | null>(null);
  const [selectedSectorSlug, setSelectedSectorSlug] = useState<string | null>(null);

  const hasActiveQuickSearch = useMemo(
    () => Boolean(searchQuery.trim() || debouncedSearch.trim() || selectedSectorSlug),
    [searchQuery, debouncedSearch, selectedSectorSlug],
  );
  const smoothLoading = useSmoothLoading({
    loading,
    showDelayMs: LOADER_SHOW_DELAY_MS,
    minVisibleMs: LOADER_MIN_VISIBLE_MS,
    hasPreviousData: companies.length > 0,
  });
  const shouldForceVerification =
    isAuthenticated &&
    isMyDiscoveryRoute &&
    !isPartnerPortalDiscoveryRoute &&
    shouldPromptCompanyRegistryVerification(userDetails);
  const canApplyForVerification = shouldForceVerification;

  // Modal auto-opening is handled globally by AppLayout.

  /** Hydrate filters from URL (browser back/forward, shared links). Layout phase avoids sync effect clearing params before state is applied. */
  useLayoutEffect(() => {
    skipNextUrlSyncRef.current = true;
    const params = new URLSearchParams(location.search);
    const urlKeyword = params.get("keyword")?.trim() ?? "";
    const urlSectorSlug = params.get("sector")?.trim() || null;
    const urlPage = params.get("page");

    setSearchQuery(urlKeyword);

    if (urlSectorSlug) {
      const sector = getSectorBySlug(urlSectorSlug);
      setSelectedSectorSlug(urlSectorSlug);
      setSelectedSectorTitle(sector?.title ?? null);
    } else {
      setSelectedSectorSlug(null);
      setSelectedSectorTitle(null);
    }

    let nextPage = 1;
    if (urlPage) {
      const p = parseInt(urlPage, 10);
      if (!Number.isNaN(p)) nextPage = Math.max(1, p);
    }
    setPage(nextPage);
    hydratedPageRef.current = nextPage;
    hasHydratedFromUrlRef.current = true;
  }, [location.search, location.pathname]);

  /** Keep discover filters in the URL so returning from a company profile restores them. */
  useEffect(() => {
    if (!hasHydratedFromUrlRef.current) return;
    if (!isCompaniesTab) return;
    if (skipNextUrlSyncRef.current) {
      skipNextUrlSyncRef.current = false;
      return;
    }

    const params = new URLSearchParams();
    const q = searchQuery.trim();
    if (q) params.set("keyword", q);
    if (selectedSectorSlug) params.set("sector", selectedSectorSlug);
    if (page > 1) params.set("page", String(page));
    const currentTab = new URLSearchParams(location.search).get("tab");
    if (currentTab) params.set("tab", currentTab);

    const nextStr = params.toString();
    const curStr = new URLSearchParams(location.search).toString();
    if (nextStr !== curStr) {
      setSearchParams(params, { replace: true });
    }
  }, [
    searchQuery,
    selectedSectorSlug,
    page,
    location.search,
    setSearchParams,
    isCompaniesTab,
  ]);

  useEffect(() => {
    if (!isGuestDiscoverRoute) {
      setGuestShortlistedIds(new Set());
      return;
    }
    setGuestShortlistedIds(new Set(readGuestShortlistCompanyIds()));
  }, [isGuestDiscoverRoute]);

  const displayCompanies = useMemo(
    () =>
      companies.map((company) => ({
        ...company,
        isShortlisted: isAuthenticated
          ? shortlistedIds.has(company.id)
          : guestShortlistedIds.has(company.id),
      })),
    [companies, shortlistedIds, guestShortlistedIds, isAuthenticated],
  );

  const discoverPromoSlides = hasPromoCarouselOverride ? promoCarouselSlides! : fetchedPromoSlides;

  // Accepts explicit page to avoid stale closure when called after page reset
  const DISCOVER_LIST_STALE_MS = 5 * 60_000;

  const fetchCompanies = async (pageToFetch = page) => {
    if (retryCount >= MAX_RETRIES) {
      setLoading(false);
      return;
    }

    try {
      const pageForRequest = isAuthenticated ? pageToFetch : 1;
      const mode = resolveDiscoverMode();
      const browseParams = {
        mode,
        page: pageForRequest,
        limit: effectivePageSize,
        expoSignupSlug,
      };
      const queryKey = discoverBrowseQueryKey(browseParams);
      const requestKey = JSON.stringify(queryKey);
      if (inFlightListAllKeyRef.current === requestKey) return;
      inFlightListAllKeyRef.current = requestKey;

      const cached = queryClient.getQueryData<{
        companies: CompanyWithUI[];
        totalPages: number;
        matchCount: number;
      }>(queryKey);
      if (cached) {
        setCompanies(cached.companies);
        setTotalPages(cached.totalPages);
        setMatchCount(cached.matchCount);
        setLoading(false);
      }

      const updatedAt = queryClient.getQueryState(queryKey)?.dataUpdatedAt ?? 0;
      const isFresh = Boolean(cached) && Date.now() - updatedAt < DISCOVER_LIST_STALE_MS;
      if (isFresh) {
        inFlightListAllKeyRef.current = null;
        return;
      }

      if (!cached) setLoading(true);
      setLoadError(null);
      if (!cached) setMatchCount(null);

      const shortlistContext = {
        isAuthenticated,
        guestShortlistedIds,
        shortlistedIds,
      };

      const result = await queryClient.fetchQuery({
        queryKey,
        staleTime: DISCOVER_LIST_STALE_MS,
        queryFn: () => fetchDiscoverBrowse(browseParams, shortlistContext),
      });

      setCompanies(result.companies);
      setTotalPages(result.totalPages);
      setMatchCount(result.matchCount);
      setRetryCount(0);
    } catch (err) {
      handleTrpcError(err);
      const nextRetry = retryCount + 1;
      setRetryCount(nextRetry);
      if (nextRetry >= MAX_RETRIES) {
        setLoadError("Unable to load companies. Please try again later.");
      }
    } finally {
      inFlightListAllKeyRef.current = null;
      setLoading(false);
    }
  };

  const fetchQuickSearch = async (
    query: string,
    pageToFetch = page,
    sectorSlug: string | null = selectedSectorSlug,
  ) => {
    const mode = resolveDiscoverMode();
    const searchParams = {
      mode,
      page: pageToFetch,
      limit: effectivePageSize,
      query,
      sectorSlug,
      expoSignupSlug,
    };
    const queryKey = discoverSearchQueryKey(searchParams);
    const requestKey = JSON.stringify(queryKey);
    if (inFlightQuickSearchKeyRef.current === requestKey) return;
    inFlightQuickSearchKeyRef.current = requestKey;
    latestQuickSearchKeyRef.current = requestKey;

    const cached = queryClient.getQueryData<{
      companies: CompanyWithUI[];
      totalPages: number;
      matchCount: number;
    }>(queryKey);
    if (cached) {
      setCompanies(cached.companies);
      setTotalPages(cached.totalPages);
      setMatchCount(cached.matchCount);
      setLoading(false);
    }

    const updatedAt = queryClient.getQueryState(queryKey)?.dataUpdatedAt ?? 0;
    const isFresh = Boolean(cached) && Date.now() - updatedAt < DISCOVER_LIST_STALE_MS;
    if (isFresh) {
      inFlightQuickSearchKeyRef.current = null;
      return;
    }

    if (!cached) setLoading(true);
    setLoadError(null);
    if (!cached) setMatchCount(null);

    try {
      const shortlistContext = {
        isAuthenticated,
        guestShortlistedIds,
        shortlistedIds,
      };

      const result = await queryClient.fetchQuery({
        queryKey,
        staleTime: DISCOVER_LIST_STALE_MS,
        queryFn: () => fetchDiscoverSearch(searchParams, shortlistContext),
      });

      if (latestQuickSearchKeyRef.current !== requestKey) return;

      setCompanies(result.companies);
      setTotalPages(result.totalPages);
      setMatchCount(result.matchCount);
    } catch (err) {
      if (latestQuickSearchKeyRef.current !== requestKey) return;
      handleTrpcError(err);
      setLoadError("Quick search failed. Please try again.");
    } finally {
      if (inFlightQuickSearchKeyRef.current === requestKey) {
        inFlightQuickSearchKeyRef.current = null;
      }
      if (latestQuickSearchKeyRef.current === requestKey) {
        setLoading(false);
      }
    }
  };

  // Triggers on search query / sector change. Restores hydrated page on remount;
  // resets to page 1 only when the user actually changes keyword or sector.
  useEffect(() => {
    if (!isCompaniesTab) {
      setLoading(false);
      return;
    }
    // On back navigation with URL filters, wait for URL -> state hydration first.
    // This prevents an initial unfiltered listAll call before filters are restored.
    if (!hasHydratedFromUrlRef.current && location.search) return;

    const rawQuery = searchQuery.trim();
    const q = debouncedSearch.trim();
    const hasQuickCriteria = Boolean(q || selectedSectorSlug);
    // Prevent a transient "load all" fetch while URL/state hydration restores the query
    // but the debounced value has not caught up yet.
    if (rawQuery && rawQuery !== q) return;

    // First render still has empty state even after layout hydration scheduled setState.
    // Wait until search/sector state matches the URL before recording criteria or fetching.
    if (lastFetchedCriteriaRef.current === null) {
      const urlParams = new URLSearchParams(location.search);
      const urlKeyword = urlParams.get("keyword")?.trim() ?? "";
      const urlSectorSlug = urlParams.get("sector")?.trim() || null;
      if (urlKeyword && !rawQuery) return;
      if (urlSectorSlug && !selectedSectorSlug) return;
    }

    const criteriaKey = `${q}|${selectedSectorSlug ?? ""}`;
    const prevCriteria = lastFetchedCriteriaRef.current;
    const isCriteriaChange = prevCriteria !== null && prevCriteria !== criteriaKey;
    lastFetchedCriteriaRef.current = criteriaKey;

    const pageToFetch = isCriteriaChange ? 1 : hydratedPageRef.current;
    if (isCriteriaChange) {
      setPage(1);
      hydratedPageRef.current = 1;
    }

    const requestKey = `${criteriaKey}|${pageToFetch}|${effectivePageSize}`;
    if (lastListRequestKeyRef.current === requestKey) return;
    lastListRequestKeyRef.current = requestKey;

    setRetryCount(0);
    setLoadError(null);

    if (!hasQuickCriteria) {
      void fetchCompanies(pageToFetch);
      return;
    }

    void fetchQuickSearch(q, pageToFetch, selectedSectorSlug);
  }, [
    searchQuery,
    debouncedSearch,
    selectedSectorSlug,
    isCompaniesTab,
  ]);

  useEffect(() => {
    if (!isCompaniesTab) return;
    if (!hasHydratedFromUrlRef.current && location.search) return;

    const prevSize = prevEffectivePageSizeRef.current;
    if (prevSize === effectivePageSize) return;

    const rawQuery = searchQuery.trim();
    const q = debouncedSearch.trim();
    if (rawQuery && rawQuery !== q) {
      prevEffectivePageSizeRef.current = effectivePageSize;
      return;
    }

    const nextPage = remapPageIndex(page, prevSize, effectivePageSize);
    prevEffectivePageSizeRef.current = effectivePageSize;
    setPage(nextPage);
    hydratedPageRef.current = nextPage;
    lastListRequestKeyRef.current = `${q}|${selectedSectorSlug ?? ""}|${nextPage}|${effectivePageSize}`;

    const hasQuick = Boolean(q || selectedSectorSlug);
    if (hasQuick) {
      void fetchQuickSearch(q, nextPage, selectedSectorSlug);
      return;
    }
    void fetchCompanies(nextPage);
  }, [effectivePageSize, isCompaniesTab]);

  const handleRunSearch = () => {
    const query = searchQuery.trim();
    setPage(1);
    hydratedPageRef.current = 1;
    lastFetchedCriteriaRef.current = `${query}|${selectedSectorSlug ?? ""}`;
    lastListRequestKeyRef.current = `${query}|${selectedSectorSlug ?? ""}|1|${effectivePageSize}`;
    setRetryCount(0);
    setLoadError(null);

    const hasQuick = Boolean(query || selectedSectorSlug);
    if (!hasQuick) {
      void fetchCompanies(1);
      return;
    }

    void fetchQuickSearch(query, 1, selectedSectorSlug);
  };

  const handlePaginationChange = (nextPage: number) => {
    if (!isAuthenticated && nextPage > 1) {
      setPaginationSignupModalOpened(true);
      return;
    }
    setPage(nextPage);
    hydratedPageRef.current = nextPage;
    const queryForKey = debouncedSearch.trim();
    lastListRequestKeyRef.current = `${queryForKey}|${selectedSectorSlug ?? ""}|${nextPage}|${effectivePageSize}`;
    // Keep the user at the table header when paging.
    requestAnimationFrame(() => {
      tableHeaderAnchorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    const query = debouncedSearch.trim();
    const hasQuick = Boolean(query || selectedSectorSlug);
    if (hasQuick) {
      void fetchQuickSearch(query, nextPage, selectedSectorSlug);
      return;
    }
    void fetchCompanies(nextPage);
  };

  const handlePageSizeChange = (next: number) => {
    setPageSize(next);
    requestAnimationFrame(() => {
      tableHeaderAnchorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const updateMobileDocked = useCallback(() => {
    if (isLgUp) return;
    const el = mobileDockSentinelRef.current;
    if (!el) {
      setMobileDocked(true);
      return;
    }
    const top = el.getBoundingClientRect().top;
    setMobileDocked(top <= window.innerHeight - MOBILE_PROMO_STRIP_PX + 2);
  }, [isLgUp]);

  const buildDiscoverProfileSearch = useCallback(() => {
    if (expoSignupSlug) return `?fromExpo=${encodeURIComponent(expoSignupSlug)}`;
    const params = new URLSearchParams();
    const q = searchQuery.trim();
    if (q) params.set("keyword", q);
    if (selectedSectorSlug) params.set("sector", selectedSectorSlug);
    if (page > 1) params.set("page", String(page));
    const s = params.toString();
    return s ? `?${s}` : "";
  }, [searchQuery, selectedSectorSlug, page, expoSignupSlug]);

  const handleClearSearch = () => {
    setSearchQuery("");
    setPage(1);
    hydratedPageRef.current = 1;
  };

  const searchClearButton = searchQuery ? (
    <ActionIcon
      variant="subtle"
      color="gray"
      size="sm"
      aria-label="Clear search"
      onClick={handleClearSearch}
    >
      <IconX size={16} />
    </ActionIcon>
  ) : undefined;

  useLayoutEffect(() => {
    if (isLgUp || !shouldShowPromoCarousel) return undefined;
    updateMobileDocked();
    window.addEventListener("scroll", updateMobileDocked, { passive: true });
    window.addEventListener("resize", updateMobileDocked);
    return () => {
      window.removeEventListener("scroll", updateMobileDocked);
      window.removeEventListener("resize", updateMobileDocked);
    };
  }, [
    isLgUp,
    updateMobileDocked,
    companies.length,
    page,
    effectivePageSize,
    totalPages,
    loading,
    loadError,
    debouncedSearch,
    shouldShowPromoCarousel,
  ]);

  const searchRow = (
    <Flex
      direction={{ base: "column", sm: "row" }}
      align={{ base: "stretch", sm: "flex-end" }}
      gap={{ base: rem(6), sm: "sm" }}
      data-discover-tour="search"
    >
      <TextInput
        placeholder={searchPlaceholder}
        leftSection={<IconSearch size={18} />}
        rightSection={searchClearButton}
        size="md"
        radius="md"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.currentTarget.value)}
        style={{ flex: isSmUp ? 1 : "0 0 auto", minWidth: 0, width: "100%" }}
      />
      <Button
        color="#04305c"
        radius="md"
        size="md"
        px="xl"
        onClick={handleRunSearch}
        fullWidth={!isSmUp}
        style={{ flex: "0 0 auto", minWidth: isSmUp ? 170 : "100%" }}
        leftSection={<IconSearch size={18} />}
      >
        Search
      </Button>
    </Flex>
  );

  const isExploreTabSwitch = isExploreTabSwitchState(location.state);

  // Mobile tab switch: keep the hero but land on the new tab's content, just under the fixed header.
  useEffect(() => {
    if (!isExploreTabSwitch) return;
    // Clear the one-shot flag so a reload or back/forward to this entry does not re-scroll.
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    if (!showExploreTabs) return;
    if (!window.matchMedia("(max-width: 47.99em)").matches) return;
    const navOffset = isAuthenticated
      ? AUTH_MOBILE_HEADER_BASE_PX + AUTH_MOBILE_EXPLORE_ROW_HEIGHT
      : landingNavbarOffset(true);
    // Not cancelled on cleanup: clearing the flag above re-runs this effect immediately.
    pinScrollToAnchor(() => tabContentAnchorRef.current, navOffset + TAB_SWITCH_SCROLL_GAP_PX);
  }, [isExploreTabSwitch, showExploreTabs, isAuthenticated, navigate, location.pathname, location.search]);

  const scrollToCompanies = useCallback(() => {
    tableHeaderAnchorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const handleExploreOpportunities = useCallback(() => {
    if (showExploreTabs && activeTab !== "companies") {
      setActiveTab("companies");
      window.setTimeout(() => {
        scrollToCompanies();
      }, 0);
      return;
    }
    scrollToCompanies();
  }, [showExploreTabs, activeTab, setActiveTab, scrollToCompanies]);

  const mobilePromoBar = (
    <Box px={0} style={{ pointerEvents: "auto" }}>
      <Paper
        radius={0}
        withBorder={false}
        shadow="none"
        bg="transparent"
        p={5}
        w="100%"
        style={{
          overflow: "hidden",
        }}
      >
        {showPromoCarouselPlaceholder ? (
          <DiscoverPromoCarouselPlaceholder layout="mobile-strip" />
        ) : (
          <DiscoverPromoCarouselMobile slides={discoverPromoSlides} height={70} autoplayDelayMs={4500} />
        )}
      </Paper>
    </Box>
  );

  const effectiveLoading = smoothLoading;
  /** Only replace the grid with shimmer on initial load — keep previous rows while refetching. */
  const showResultsShimmer = effectiveLoading && companies.length === 0;

  const discoverMainBody = (
    <DiscoverResultsSection
      companies={displayCompanies}
      selectedSectorTitle={selectedSectorTitle}
      debouncedSearch={debouncedSearch}
      isAuthenticated={isAuthenticated}
      loggedInProfilePathBuilder={
        isFacilitatorDiscoveryRoute
          ? (id) => `/facilitator/companies/${id}/profile`
          : isEcosystemHeadDiscoveryRoute
            ? (id) => `/ecosystem-head/companies/${id}/profile`
            : isExpertDiscoveryRoute
              ? (id) => `/expert/companies/${id}/profile`
              : isExpoHeadDiscoveryRoute
                ? (id) => `/expo-head/companies/${id}/profile`
                : undefined
      }
      loading={loading}
      showResultsShimmer={showResultsShimmer}
      shimmerCount={effectivePageSize}
      matchCount={matchCount}
      loadError={loadError}
      isHomeRoute={isHomeRoute}
      totalPages={totalPages}
      page={page}
      effectivePageSize={effectivePageSize}
      pageSizeOptions={
        lockDiscoverPageSize ? [DEFAULT_PAGE_SIZE] : DISCOVER_PAGE_SIZE_OPTIONS
      }
      onPageChange={handlePaginationChange}
      onPageSizeChange={lockDiscoverPageSize ? undefined : handlePageSizeChange}
      addToShortlist={addToShortlist}
      removeFromShortlist={removeFromShortlist}
      setCompanies={setCompanies}
      setGuestShortlistedIds={setGuestShortlistedIds}
      buildDiscoverProfileSearch={buildDiscoverProfileSearch}
      isMyDiscoveryRoute={isMyDiscoveryRoute}
      tableHeaderAnchorRef={tableHeaderAnchorRef}
      gridContainerRef={gridContainerRef}
      canViewCompanyDetails={!shouldForceVerification}
      onBlockedViewCompanyDetails={openVerificationModal}
      searchRow={isGuestDiscoverRoute ? searchRow : undefined}
      showSearchInSection={isGuestDiscoverRoute}
      isGuestDiscoverGrid={useCompactCompanyGrid}
      afterFirstRow={<PageCtaAfterFirstRow pageKey="DISCOVER" />}
      firstRowCount={companyFirstRowCount}
    />
  );

  // Auth explore pills live in AppLayout's global header; guest lists use LandingNavbar.
  // Network stats hero is shared across companies/experts/corridors/expos (guest + auth).
  const showAuthExploreStatsHero =
    isClientMyDiscoveryRoute && isAuthenticated && !expoSignupLocked;
  const showAuthExploreSubtitle =
    isClientMyDiscoveryRoute && isAuthenticated && !isPartnerPortalDiscoveryRoute;

  const discoverTagline = "Trusted Companies. Better Partnerships.";

  const heroTitle = (
    <Title
      order={2}
      fz={{ base: 24, sm: 28, md: 32 }}
      fw={700}
      lh={1.15}
      style={{ minWidth: 0 }}
    >
      {discoverTagline}
    </Title>
  );

  const promoPanelDesktop = shouldShowPromoCarousel ? (
    <Paper
      radius="md"
      p={0}
      withBorder
      shadow="xs"
      bg="primary.0"
      w="100%"
      h="100%"
      style={{
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
      }}
    >
      {showPromoCarouselPlaceholder ? (
        <DiscoverPromoCarouselPlaceholder layout="desktop-aside" />
      ) : (
        <DiscoverPromoCarouselDesktop
          key="discover-promo-carousel-desktop"
          slides={discoverPromoSlides}
          compact
          fillHeight
          height={200}
          autoplayDelayMs={4500}
        />
      )}
    </Paper>
  ) : null;

  return (
    <Box
      pb={{
        base: shouldShowPromoCarousel && !mobileDocked ? MOBILE_PROMO_STRIP_RESERVE : rem(8),
        lg: rem(40),
      }}
    >
      {promoPopPageKey ? <PromoPopHost pageKey={promoPopPageKey} /> : null}

      {/* Mobile Header (kept for non-guest Discover variants) */}
      {!isHomeRoute && !isMyDiscoveryRoute && !isGuestDiscoverRoute ? (
        <Box py="md" hiddenFrom="sm">
          <Container size={DISCOVER_PAGE_CONTENT_SIZE} px={DISCOVER_PAGE_CONTENT_PX}>
            <Title order={2} fz={20} fw={600}>
            Explore verified companies with advanced search criteria.
            </Title>
          </Container>
        </Box>
      ) : null}

      {isGuestDiscoverRoute ? (
        <Box
          w="100%"
          style={{
            background: "linear-gradient(180deg, #EAF4FB 0%, #F4F9FD 55%, #FFFFFF 100%)",
          }}
          pt={{ base: "xs", sm: 10 }}
          pb={0}
        >
          <Container fluid px={DISCOVER_PAGE_CONTENT_PX}>
            <DiscoverActiveCollaborationHero onExploreAll={handleExploreOpportunities} />
          </Container>
        </Box>
      ) : null}

      <Container
        fluid
        size={DISCOVER_PAGE_CONTENT_SIZE}
        px={isGuestDiscoverRoute || showAuthExploreStatsHero ? DISCOVER_PAGE_CONTENT_PX : undefined}
        py={{ base: isMyDiscoveryRoute ? "md" : 0, sm: isGuestDiscoverRoute ? 0 : 40 }}
      >
        {showAuthExploreStatsHero ? (
          <Box mb="md">
            <Group justify="space-between" align="flex-start" gap="sm" wrap="wrap">
              <Box style={{ flex: "1 1 auto", minWidth: 0 }}>
                <DiscoverActiveCollaborationHero variant="auth" />
              </Box>
              {canApplyForVerification ? (
                <Button bg="green" size="xs" onClick={openVerificationModal} style={{ flexShrink: 0 }}>
                  Apply for verification
                </Button>
              ) : null}
            </Group>
          </Box>
        ) : null}

        <div ref={tabContentAnchorRef} aria-hidden />

        {showAuthExploreSubtitle && activeTab !== "companies" ? (
          <Text size="sm" c="dimmed" mb="xs">
            {EXPLORE_TAB_SUBTITLES[activeTab]}
          </Text>
        ) : null}

        {isCompaniesTab ? <PageCtaSlotList pageKey="DISCOVER" slotKey="TOP_BANNER" /> : null}

        <Stack gap="md" mb={isCompaniesTab ? (isGuestDiscoverRoute ? 0 : isHomeRoute ? 0 : "lg") : 0}>
          {isCompaniesTab ? (
            <>
              {!isGuestDiscoverRoute && shouldShowPromoCarousel ? (
                <>
                  <Flex
                    gap="xl"
                    align="stretch"
                    justify="space-between"
                    wrap="wrap"
                    direction="row"
                    w="100%"
                    visibleFrom="lg"
                  >
                    <Stack gap="md" miw={0} justify="flex-start" style={{ flex: "1 1 0" }}>
                      {!showAuthExploreSubtitle ? heroTitle : null}
                    </Stack>
                    <Box
                      component="aside"
                      aria-label="Featured collaborations"
                      w={380}
                      maw="42%"
                      miw={300}
                      style={{ flexShrink: 0, display: "flex", flexDirection: "column", minHeight: 0 }}
                    >
                      {promoPanelDesktop}
                    </Box>
                  </Flex>
                  <Stack gap="md" hiddenFrom="lg">
                    {!showAuthExploreSubtitle ? heroTitle : null}
                  </Stack>
                </>
              ) : !isGuestDiscoverRoute ? (
                <>{!showAuthExploreSubtitle ? heroTitle : null}</>
              ) : null}

              {!isGuestDiscoverRoute ? searchRow : null}
            </>
          ) : null}
        </Stack>

        {isCompaniesTab ? (
          <>
            {!hasActiveQuickSearch && !expoSignupLocked ? (
              <DiscoverGlobalEcosystemsSection
                mt={isGuestDiscoverRoute ? 2 : isHomeRoute ? "xs" : "lg"}
                mb={isGuestDiscoverRoute ? "lg" : isHomeRoute ? "sm" : "lg"}
              />
            ) : null}

            {discoverMainBody}

            {shouldShowPromoCarousel ? (
              <div ref={mobileDockSentinelRef} style={{ height: 0, width: "100%" }} aria-hidden />
            ) : null}

            {shouldShowPromoCarousel && mobileDocked ? (
              <Box component="aside" mt="lg" aria-label="Featured collaborations" hiddenFrom="lg">
                {mobilePromoBar}
              </Box>
            ) : null}
          </>
        ) : null}

        {showExploreTabs && activeTab === "experts" ? (
          <Box mih={TAB_BODY_MOBILE_MIN_H}>
            <PageCtaSlotList pageKey="EXPERTS" slotKey="TOP_BANNER" />
            <ExploreTabSuspense>
              {isGuestDiscoverRoute ? <GuestExpertsExploreColumn /> : <ExpertsColumn />}
            </ExploreTabSuspense>
          </Box>
        ) : null}

        {showExploreTabs && activeTab === "corridors" ? (
          <Box mih={TAB_BODY_MOBILE_MIN_H}>
            <PageCtaSlotList pageKey="CORRIDORS" slotKey="TOP_BANNER" />
            <ExploreTabSuspense>
              {isGuestDiscoverRoute ? <GuestCorridorsExploreColumn /> : <CorridorsColumn />}
            </ExploreTabSuspense>
          </Box>
        ) : null}

        {showExploreTabs && activeTab === "expos" ? (
          <Box mih={TAB_BODY_MOBILE_MIN_H}>
            <ExploreTabSuspense>
              <DiscoverExpoSection includePast enabled />
            </ExploreTabSuspense>
          </Box>
        ) : null}
      </Container>

      {shouldShowPromoCarousel && !mobileDocked ? (
        <Box
          pos="fixed"
          left={0}
          right={0}
          bottom={0}
          pb="max(0px, env(safe-area-inset-bottom, 0px))"
          style={{ zIndex: 40, pointerEvents: "none" }}
          aria-label="Featured collaborations"
          hiddenFrom="lg"
        >
          {mobilePromoBar}
        </Box>
      ) : null}

      <PaginationSignupModal
        opened={paginationSignupModalOpened}
        onClose={() => setPaginationSignupModalOpened(false)}
        flow="pagination"
      />
    </Box>
  );
}
